import React, { useEffect, useRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { Icon } from '@components/icons';

const StyledServicesSection = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  /* Using global .numbered-heading styles */

  .services-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 25px 20px;
    width: 100%;

    @media (max-width: 1080px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const StyledService = styled.li`
  position: relative;
  transition: var(--transition);

  .service-inner {
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem 1.75rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 30px -15px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-10px) scale(1.03);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 42, 42, 0.9);
      box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.7), 0 0 25px rgba(255, 42, 42, 0.35);

      .service-title {
        color: #ff2a2a;
      }
    }

    @media (max-width: 768px) {
      padding: 30px 24px !important;
      border-radius: 20px !important;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.04) 0%,
        rgba(255, 255, 255, 0.01) 100%
      ) !important;
      border: 1px solid rgba(255, 255, 255, 0.08) !important;
      backdrop-filter: blur(16px) !important;
      -webkit-backdrop-filter: blur(16px) !important;
      box-shadow: 0 15px 35px -20px rgba(0, 0, 0, 0.6) !important;

      &:hover {
        border-color: rgba(255, 42, 42, 0.9) !important;
      }
    }
  }

  header {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .service-top {
    ${({ theme }) => theme.mixins.flexBetween};
    margin-bottom: 30px;

    .folder {
      color: #ff2a2a;
      margin: auto;
      svg {
        width: 50px;
        height: 50px;
        transition: var(--transition);
      }
    }

    @media (max-width: 768px) {
      margin-bottom: 20px !important;

      .folder {
        svg {
          width: 44px !important;
          height: 44px !important;
          filter: drop-shadow(0 4px 10px rgba(255, 42, 42, 0.25)) !important;
        }
      }
    }
  }

  .service-title {
    margin: 0 0 15px;
    color: var(--white);
    font-size: var(--fz-xl);
    text-align: center;
    font-weight: 600;
    transition: var(--transition);
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 50px;

    @media (max-width: 768px) {
      font-size: 19px !important;
      font-weight: 700 !important;
      letter-spacing: -0.3px !important;
      margin-bottom: 12px !important;
      min-height: auto !important;
    }
  }

  .service-description {
    color: var(--light-slate);
    font-size: 16px;
    line-height: 1.5;
    text-align: center;
    flex-grow: 1;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    @media (max-width: 768px) {
      font-size: 14px !important;
      line-height: 1.62 !important;
      color: var(--lightest-slate) !important;
    }
  }
`;

const Services = () => {
  const data = useStaticQuery(graphql`
    query {
      services: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/services/" }
          frontmatter: { showInServices: { ne: false } }
        }
        sort: { fields: [frontmatter___order], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              icon
            }
            html
          }
        }
      }
    }
  `);

  const revealContainer = useRef(null);
  const revealTitle = useRef(null);
  const revealServices = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealContainer.current, srConfig());
    sr.reveal(revealTitle.current, srConfig());
    revealServices.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, [prefersReducedMotion]);

  const GRID_LIMIT = 6;
  const services = data.services.edges.filter(({ node }) => node);
  const firstSix = services.slice(0, GRID_LIMIT);

  return (
    <StyledServicesSection id="services" ref={revealContainer}>
      <h2 className="numbered-heading" ref={revealTitle}>
        What I Do
      </h2>

      <ul className="services-grid">
        {prefersReducedMotion ? (
          <>
            {firstSix.map(({ node }, i) => (
              <StyledService key={i}>
                <div className="service-inner">
                  <header>
                    <div className="service-top">
                      <div className="folder">
                        <Icon name={node.frontmatter.icon} />
                      </div>
                    </div>
                    <h3 className="service-title">{node.frontmatter.title}</h3>
                    <div
                      className="service-description"
                      dangerouslySetInnerHTML={{ __html: node.html }}
                    />
                  </header>
                </div>
              </StyledService>
            ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {firstSix.map(({ node }, i) => (
              <CSSTransition
                key={i}
                classNames="fadeup"
                timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                exit={false}
              >
                <StyledService
                  key={i}
                  ref={el => (revealServices.current[i] = el)}
                  style={{
                    transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                  }}
                >
                  <div className="service-inner">
                    <header>
                      <div className="service-top">
                        <div className="folder">
                          <Icon name={node.frontmatter.icon} />
                        </div>
                      </div>
                      <h3 className="service-title">{node.frontmatter.title}</h3>
                      <div
                        className="service-description"
                        dangerouslySetInnerHTML={{ __html: node.html }}
                      />
                    </header>
                  </div>
                </StyledService>
              </CSSTransition>
            ))}
          </TransitionGroup>
        )}
      </ul>
    </StyledServicesSection>
  );
};

export default Services;
